import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaBan,
  FaBookOpen,
  FaCheckCircle,
  FaCrown,
  FaExclamationCircle,
  FaGraduationCap,
  FaIdCard,
  FaRedo,
  FaServer,
  FaShieldAlt,
} from "react-icons/fa";

import { useAuth } from "../AuthContext";
import UnSubscribe from "../Subscription/UnSubscribe";

import "./ProfileStyles.css";

type AccessMap = {
  full?: boolean;
  A1?: boolean;
  A2?: boolean;
  B1?: boolean;
  B2?: boolean;
  C1?: boolean;
  C2?: boolean;
};

type AccessResponse = {
  id?: number;
  email?: string;
  source?: string;
  note?: string | null;
  is_active?: boolean;
  has_record?: boolean;

  manual_checked?: boolean;
  manual_has_record?: boolean;

  revenuecat_checked?: boolean;
  revenuecat_configured?: boolean;
  revenuecat_customer_ids?: string[];
  revenuecat_matched_emails?: string[];
  revenuecat_active_entitlements?: string[];
  revenuecat_active_products?: string[];
  revenuecat_subscription_ids?: string[];
  revenuecat_error?: string | null;

  has_full_access?: boolean;
  has_full_course_access?: boolean;
  has_full_book_access?: boolean;

  course_access_levels?: string[];
  book_access_levels?: string[];
  access_levels?: string[];

  access_map?: AccessMap;
  course_access_map?: AccessMap;
  book_access_map?: AccessMap;

  accessible_levels?: string[];
  accessible_course_levels?: string[];
  accessible_book_levels?: string[];

  total_books?: number;
  levels?: Array<{
    level?: string;
    title?: string;
    subtitle?: string;
    book_count?: number;
  }>;
};

type AccessesApiResponse =
  | AccessResponse[]
  | {
      data?: AccessResponse[];
    };

type SubscriptionStatusResponse = {
  success?: boolean;
  has_subscription?: boolean;
  customer_id?: string | null;
  subscription_id?: string | null;
  status?: string | null;
  auto_renewal_status?: string | null;
  current_period_ends_at?: number | null;
  gives_access?: boolean;
  product_keys?: string[];
  entitlement_keys?: string[];
  message?: string;
  error?: string | null;
};

const API_BASE_URL = (
  process.env.REACT_APP_BACKEND_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://www.api.dearlanguage.com"
).replace(/\/+$/, "");

const BOOK_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const COURSE_LEVELS = ["A1", "A2", "B1", "B2"];

const COURSE_NAMES: Record<string, string> = {
  A1: "A1 Deutschkurs",
  A2: "A2 Deutschkurs",
  B1: "B1 Deutschkurs",
  B2: "B2 Deutschkurs",
  C1: "C1 Deutschkurs",
  C2: "C2 Deutschkurs",
};

const WEBSITE_ENTITLEMENT =
  process.env.REACT_APP_REVENUECAT_FULL_WEBSITE_ENTITLEMENT_ID ||
  "website_full_access";

const WEBSITE_PRODUCT =
  process.env.REACT_APP_REVENUECAT_FULL_WEBSITE_PACKAGE_ID ||
  "website_full_month";

const normalizeEmail = (email?: string | null): string => {
  return String(email || "")
    .trim()
    .toLowerCase();
};

const normalizeAccessLevel = (level?: string | null): string => {
  const cleanLevel = String(level || "").trim();

  if (!cleanLevel) {
    return "";
  }

  if (cleanLevel.toLowerCase() === "full") {
    return "full";
  }

  return cleanLevel.toUpperCase();
};

const normalizeList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => String(item || "").trim())
    .filter(Boolean);
};

const normalizeLevelList = (value: unknown): string[] => {
  return normalizeList(value)
    .map(normalizeAccessLevel)
    .filter(Boolean);
};

const uniqueList = (...items: string[][]): string[] => {
  return Array.from(
    new Set(
      items
        .flat()
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );
};

const filterLevels = (levels: string[], allowedLevels: string[]): string[] => {
  const normalized = levels
    .map(normalizeAccessLevel)
    .filter((level) => allowedLevels.includes(level));

  return allowedLevels.filter((level) => normalized.includes(level));
};

const uniqueLevels = (
  allowedLevels: string[],
  ...items: string[][]
): string[] => {
  return filterLevels(uniqueList(...items), allowedLevels);
};

const hasValue = (values: string[], expected: string): boolean => {
  const cleanExpected = expected.trim().toLowerCase();

  return values.some((value) => {
    const cleanValue = value.trim().toLowerCase();

    return cleanValue === cleanExpected || cleanValue.includes(cleanExpected);
  });
};

const hasFullLevel = (levels: string[]): boolean => {
  return levels.some((level) => normalizeAccessLevel(level) === "full");
};

const mapHasFull = (map?: AccessMap | null): boolean => {
  return Boolean(map?.full);
};

const mapToLevels = (
  map: AccessMap | undefined | null,
  allowedLevels: string[],
): string[] => {
  if (!map) {
    return [];
  }

  if (map.full) {
    return allowedLevels;
  }

  return allowedLevels.filter((level) => Boolean(map[level as keyof AccessMap]));
};

const isAccessActive = (access: AccessResponse | null): boolean => {
  if (!access) {
    return false;
  }

  return access.is_active !== false;
};

const accessHasFullCourseAccess = (access: AccessResponse | null): boolean => {
  if (!isAccessActive(access)) {
    return false;
  }

  const courseLevels = normalizeLevelList(access?.course_access_levels);
  const accessLevels = normalizeLevelList(access?.access_levels);
  const accessibleCourseLevels = normalizeLevelList(
    access?.accessible_course_levels,
  );

  return Boolean(
    access?.has_full_access ||
      access?.has_full_course_access ||
      mapHasFull(access?.access_map) ||
      mapHasFull(access?.course_access_map) ||
      hasFullLevel(courseLevels) ||
      hasFullLevel(accessLevels) ||
      hasFullLevel(accessibleCourseLevels),
  );
};

const accessHasFullBookAccess = (access: AccessResponse | null): boolean => {
  if (!isAccessActive(access)) {
    return false;
  }

  const bookLevels = normalizeLevelList(access?.book_access_levels);
  const accessLevels = normalizeLevelList(access?.access_levels);
  const accessibleBookLevels = normalizeLevelList(access?.accessible_book_levels);
  const accessibleLevels = normalizeLevelList(access?.accessible_levels);

  return Boolean(
    access?.has_full_access ||
      access?.has_full_book_access ||
      mapHasFull(access?.access_map) ||
      mapHasFull(access?.book_access_map) ||
      hasFullLevel(bookLevels) ||
      hasFullLevel(accessLevels) ||
      hasFullLevel(accessibleBookLevels) ||
      hasFullLevel(accessibleLevels),
  );
};

const getCourseLevelsFromAccess = (
  access: AccessResponse | null,
): string[] => {
  if (!isAccessActive(access)) {
    return [];
  }

  if (accessHasFullCourseAccess(access)) {
    return COURSE_LEVELS;
  }

  return uniqueLevels(
    COURSE_LEVELS,
    normalizeLevelList(access?.accessible_course_levels),
    normalizeLevelList(access?.course_access_levels),
    normalizeLevelList(access?.access_levels),
    normalizeLevelList(access?.accessible_levels),
    mapToLevels(access?.course_access_map, COURSE_LEVELS),
    mapToLevels(access?.access_map, COURSE_LEVELS),
  );
};

const getBookLevelsFromAccess = (access: AccessResponse | null): string[] => {
  if (!isAccessActive(access)) {
    return [];
  }

  if (accessHasFullBookAccess(access)) {
    return BOOK_LEVELS;
  }

  return uniqueLevels(
    BOOK_LEVELS,
    normalizeLevelList(access?.accessible_book_levels),
    normalizeLevelList(access?.book_access_levels),
    normalizeLevelList(access?.access_levels),
    normalizeLevelList(access?.accessible_levels),
    mapToLevels(access?.book_access_map, BOOK_LEVELS),
    mapToLevels(access?.access_map, BOOK_LEVELS),
  );
};

const formatDate = (value?: string | null): string => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const formatTimestampDate = (value?: number | null): string => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "medium",
  }).format(date);
};

const formatSource = (source?: string): string => {
  const cleanSource = String(source || "").trim().toLowerCase();

  if (cleanSource === "revenuecat") {
    return "RevenueCat";
  }

  if (cleanSource === "manual") {
    return "Manuell";
  }

  if (cleanSource === "manual_revenuecat") {
    return "Manuell + RevenueCat";
  }

  return source || "Nicht erkannt";
};

const listOrEmpty = (items: string[]): string => {
  return items.length > 0 ? items.join(", ") : "Keine";
};

const extractAccesses = (value: AccessesApiResponse): AccessResponse[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  return [];
};

const findManualAccessForEmail = (
  records: AccessResponse[],
  email: string,
): AccessResponse | null => {
  const cleanEmail = normalizeEmail(email);

  if (!cleanEmail) {
    return null;
  }

  return (
    records.find((record) => {
      return normalizeEmail(record.email) === cleanEmail && record.is_active !== false;
    }) || null
  );
};

const isCancelledAutoRenewal = (value?: string | null): boolean => {
  const cleanValue = String(value || "").toLowerCase();

  return (
    cleanValue.includes("will_not_renew") ||
    cleanValue.includes("cancel") ||
    cleanValue.includes("canceled") ||
    cleanValue.includes("cancelled")
  );
};

const formatAutoRenewal = (value?: string | null): string => {
  if (isCancelledAutoRenewal(value)) {
    return "Gekündigt";
  }

  const cleanValue = String(value || "").trim();

  if (!cleanValue) {
    return "—";
  }

  if (cleanValue.toLowerCase() === "will_renew") {
    return "Aktiv";
  }

  return cleanValue;
};

const formatSubscriptionStatus = (value?: string | null): string => {
  const cleanValue = String(value || "").trim().toLowerCase();

  if (!cleanValue) {
    return "—";
  }

  if (cleanValue === "active") {
    return "Aktiv";
  }

  if (cleanValue === "expired") {
    return "Abgelaufen";
  }

  if (cleanValue === "cancelled" || cleanValue === "canceled") {
    return "Gekündigt";
  }

  return value || "—";
};

const Profile: React.FC = () => {
  const { user, userId, userEmail, isLoggedIn, authLoading } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);
  const [profileError, setProfileError] = useState<string>("");
  const [manualAccessError, setManualAccessError] = useState<string>("");
  const [bookAccess, setBookAccess] = useState<AccessResponse | null>(null);
  const [courseAccess, setCourseAccess] = useState<AccessResponse | null>(null);
  const [manualAccess, setManualAccess] = useState<AccessResponse | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatusResponse | null>(null);
  const [showUnsubscribe, setShowUnsubscribe] = useState<boolean>(false);

  const cleanEmail = normalizeEmail(userEmail);
  const cleanUserId = userId?.trim() || "";

  const displayName =
    user?.displayName?.trim() || cleanEmail.split("@")[0] || "Lernender";

  const createdAt = formatDate(user?.metadata?.creationTime);
  const lastSignInAt = formatDate(user?.metadata?.lastSignInTime);

  const revenueCatCustomerIds = useMemo(() => {
    return uniqueList(
      normalizeList(bookAccess?.revenuecat_customer_ids),
      normalizeList(courseAccess?.revenuecat_customer_ids),
      subscriptionStatus?.customer_id ? [subscriptionStatus.customer_id] : [],
    );
  }, [bookAccess, courseAccess, subscriptionStatus]);

  const revenueCatMatchedEmails = useMemo(() => {
    return uniqueList(
      normalizeList(bookAccess?.revenuecat_matched_emails),
      normalizeList(courseAccess?.revenuecat_matched_emails),
    );
  }, [bookAccess, courseAccess]);

  const revenueCatActiveEntitlements = useMemo(() => {
    return uniqueList(
      normalizeList(bookAccess?.revenuecat_active_entitlements),
      normalizeList(courseAccess?.revenuecat_active_entitlements),
      normalizeList(subscriptionStatus?.entitlement_keys),
    );
  }, [bookAccess, courseAccess, subscriptionStatus]);

  const revenueCatActiveProducts = useMemo(() => {
    return uniqueList(
      normalizeList(bookAccess?.revenuecat_active_products),
      normalizeList(courseAccess?.revenuecat_active_products),
      normalizeList(subscriptionStatus?.product_keys),
    );
  }, [bookAccess, courseAccess, subscriptionStatus]);

  const revenueCatSubscriptionIds = useMemo(() => {
    return uniqueList(
      normalizeList(bookAccess?.revenuecat_subscription_ids),
      normalizeList(courseAccess?.revenuecat_subscription_ids),
      subscriptionStatus?.subscription_id ? [subscriptionStatus.subscription_id] : [],
    );
  }, [bookAccess, courseAccess, subscriptionStatus]);

  const hasRevenueCatWebsitePremium = useMemo(() => {
    return Boolean(
      hasValue(revenueCatActiveEntitlements, WEBSITE_ENTITLEMENT) ||
        hasValue(revenueCatActiveProducts, WEBSITE_PRODUCT) ||
        subscriptionStatus?.gives_access ||
        subscriptionStatus?.has_subscription,
    );
  }, [
    revenueCatActiveEntitlements,
    revenueCatActiveProducts,
    subscriptionStatus,
  ]);

  const manualCourseLevels = useMemo(() => {
    return getCourseLevelsFromAccess(manualAccess);
  }, [manualAccess]);

  const manualBookLevels = useMemo(() => {
    return getBookLevelsFromAccess(manualAccess);
  }, [manualAccess]);

  const checkedCourseLevels = useMemo(() => {
    return uniqueLevels(
      COURSE_LEVELS,
      getCourseLevelsFromAccess(courseAccess),
      getCourseLevelsFromAccess(bookAccess),
    );
  }, [bookAccess, courseAccess]);

  const checkedBookLevels = useMemo(() => {
    return uniqueLevels(
      BOOK_LEVELS,
      getBookLevelsFromAccess(bookAccess),
      getBookLevelsFromAccess(courseAccess),
    );
  }, [bookAccess, courseAccess]);

  const hasManualFullCourseAccess = useMemo(() => {
    return accessHasFullCourseAccess(manualAccess);
  }, [manualAccess]);

  const hasManualFullBookAccess = useMemo(() => {
    return accessHasFullBookAccess(manualAccess);
  }, [manualAccess]);

  const hasCheckedFullCourseAccess = useMemo(() => {
    return (
      accessHasFullCourseAccess(courseAccess) ||
      accessHasFullCourseAccess(bookAccess)
    );
  }, [bookAccess, courseAccess]);

  const hasCheckedFullBookAccess = useMemo(() => {
    return accessHasFullBookAccess(bookAccess) || accessHasFullBookAccess(courseAccess);
  }, [bookAccess, courseAccess]);

  const hasFullCourseAccess = useMemo(() => {
    return hasManualFullCourseAccess || hasCheckedFullCourseAccess;
  }, [hasCheckedFullCourseAccess, hasManualFullCourseAccess]);

  const hasFullBookAccess = useMemo(() => {
    return (
      hasRevenueCatWebsitePremium ||
      hasManualFullBookAccess ||
      hasCheckedFullBookAccess
    );
  }, [
    hasCheckedFullBookAccess,
    hasManualFullBookAccess,
    hasRevenueCatWebsitePremium,
  ]);

  const accessibleCourseLevels = useMemo(() => {
    if (hasFullCourseAccess) {
      return COURSE_LEVELS;
    }

    return uniqueLevels(COURSE_LEVELS, manualCourseLevels, checkedCourseLevels);
  }, [checkedCourseLevels, hasFullCourseAccess, manualCourseLevels]);

  const accessibleBookLevels = useMemo(() => {
    if (hasFullBookAccess) {
      return BOOK_LEVELS;
    }

    return uniqueLevels(
      BOOK_LEVELS,
      manualBookLevels,
      checkedBookLevels,
      accessibleCourseLevels,
    );
  }, [
    accessibleCourseLevels,
    checkedBookLevels,
    hasFullBookAccess,
    manualBookLevels,
  ]);

  const hasManualAnyAccess = useMemo(() => {
    return (
      manualCourseLevels.length > 0 ||
      manualBookLevels.length > 0 ||
      hasManualFullCourseAccess ||
      hasManualFullBookAccess
    );
  }, [
    hasManualFullBookAccess,
    hasManualFullCourseAccess,
    manualBookLevels,
    manualCourseLevels,
  ]);

  const hasAnyAccess = useMemo(() => {
    return (
      hasRevenueCatWebsitePremium ||
      accessibleCourseLevels.length > 0 ||
      accessibleBookLevels.length > 0
    );
  }, [accessibleBookLevels, accessibleCourseLevels, hasRevenueCatWebsitePremium]);

  const profileAccessSource = useMemo(() => {
    if (hasManualAnyAccess && hasRevenueCatWebsitePremium) {
      return "Manuell + RevenueCat";
    }

    if (hasManualAnyAccess) {
      return "Manuell";
    }

    if (hasRevenueCatWebsitePremium) {
      return "RevenueCat";
    }

    return formatSource(bookAccess?.source || courseAccess?.source);
  }, [
    bookAccess?.source,
    courseAccess?.source,
    hasManualAnyAccess,
    hasRevenueCatWebsitePremium,
  ]);

  const activeSubscriptions = useMemo(() => {
    const subscriptions: string[] = [];

    if (hasRevenueCatWebsitePremium) {
      subscriptions.push(
        isCancelledAutoRenewal(subscriptionStatus?.auto_renewal_status)
          ? "Website Premium gekündigt"
          : "Website Premium aktiv",
      );
    }

    if (hasFullCourseAccess && hasFullBookAccess && hasManualAnyAccess) {
      subscriptions.push("Manueller Vollzugang");
    } else if (hasManualAnyAccess) {
      if (manualCourseLevels.length > 0) {
        subscriptions.push(`Manuell Kurse: ${manualCourseLevels.join(", ")}`);
      }

      if (manualBookLevels.length > 0) {
        subscriptions.push(`Manuell Bücher: ${manualBookLevels.join(", ")}`);
      }
    }

    if (!hasManualAnyAccess && accessibleCourseLevels.length > 0) {
      accessibleCourseLevels.forEach((level) => {
        subscriptions.push(COURSE_NAMES[level] || `${level} Deutschkurs`);
      });
    }

    return Array.from(new Set(subscriptions));
  }, [
    accessibleCourseLevels,
    hasFullBookAccess,
    hasFullCourseAccess,
    hasManualAnyAccess,
    hasRevenueCatWebsitePremium,
    manualBookLevels,
    manualCourseLevels,
    subscriptionStatus?.auto_renewal_status,
  ]);

  const unlockedBookCount = useMemo(() => {
    if (hasFullBookAccess || accessibleBookLevels.length >= BOOK_LEVELS.length) {
      return 60;
    }

    return accessibleBookLevels.length * 10;
  }, [accessibleBookLevels, hasFullBookAccess]);

  const unlockedCourseCount = useMemo(() => {
    if (hasFullCourseAccess || accessibleCourseLevels.length >= COURSE_LEVELS.length) {
      return COURSE_LEVELS.length;
    }

    return accessibleCourseLevels.length;
  }, [accessibleCourseLevels, hasFullCourseAccess]);

  const revenueCatError =
    bookAccess?.revenuecat_error || courseAccess?.revenuecat_error || "";

  const subscriptionEndDate = formatTimestampDate(
    subscriptionStatus?.current_period_ends_at,
  );

  const loadProfile = useCallback(async (): Promise<void> => {
    if (!isLoggedIn || !cleanEmail) {
      setBookAccess(null);
      setCourseAccess(null);
      setManualAccess(null);
      setSubscriptionStatus(null);
      return;
    }

    const controller = new AbortController();

    setLoading(true);
    setProfileError("");
    setManualAccessError("");

    try {
      const query = new URLSearchParams({
        email: cleanEmail,
        force_refresh: "1",
        profile_refresh: String(Date.now()),
      });

      if (cleanUserId) {
        query.set("app_user_id", cleanUserId);
      }

      const [
        booksResponse,
        coursesResponse,
        accessesResponse,
        subscriptionResponse,
      ] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/api/protected-books?${query.toString()}`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        }),
        fetch(`${API_BASE_URL}/api/access/check?${query.toString()}`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        }),
        fetch(`${API_BASE_URL}/api/accesses?manual_refresh=${Date.now()}`, {
          method: "GET",
          cache: "no-store",
          signal: controller.signal,
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        }),
        fetch(
          `${API_BASE_URL}/api/revenuecat/unsubscribe/status?${query.toString()}`,
          {
            method: "GET",
            cache: "no-store",
            signal: controller.signal,
            headers: {
              Accept: "application/json",
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          },
        ),
      ]);

      if (booksResponse.status !== "fulfilled" || !booksResponse.value.ok) {
        const status =
          booksResponse.status === "fulfilled"
            ? booksResponse.value.status
            : "Netzwerkfehler";

        throw new Error(`Bücherzugang konnte nicht geladen werden: ${status}`);
      }

      if (coursesResponse.status !== "fulfilled" || !coursesResponse.value.ok) {
        const status =
          coursesResponse.status === "fulfilled"
            ? coursesResponse.value.status
            : "Netzwerkfehler";

        throw new Error(`Kurszugang konnte nicht geladen werden: ${status}`);
      }

      const booksJson = (await booksResponse.value.json()) as AccessResponse;
      const coursesJson = (await coursesResponse.value.json()) as AccessResponse;

      let manualJson: AccessResponse | null = null;

      if (accessesResponse.status === "fulfilled" && accessesResponse.value.ok) {
        const accessesJson =
          (await accessesResponse.value.json()) as AccessesApiResponse;

        manualJson = findManualAccessForEmail(
          extractAccesses(accessesJson),
          cleanEmail,
        );
      } else {
        setManualAccessError("Manueller Zugang konnte nicht geprüft werden.");
      }

      let subscriptionJson: SubscriptionStatusResponse | null = null;

      if (
        subscriptionResponse.status === "fulfilled" &&
        subscriptionResponse.value.ok
      ) {
        subscriptionJson =
          (await subscriptionResponse.value.json()) as SubscriptionStatusResponse;
      }

      setBookAccess(booksJson);
      setCourseAccess(coursesJson);
      setManualAccess(manualJson);
      setSubscriptionStatus(subscriptionJson);

      const revenueCatPremiumForHeader = Boolean(
        hasValue(
          normalizeList(booksJson.revenuecat_active_entitlements),
          WEBSITE_ENTITLEMENT,
        ) ||
          hasValue(
            normalizeList(coursesJson.revenuecat_active_entitlements),
            WEBSITE_ENTITLEMENT,
          ) ||
          hasValue(
            normalizeList(subscriptionJson?.entitlement_keys),
            WEBSITE_ENTITLEMENT,
          ) ||
          subscriptionJson?.gives_access ||
          subscriptionJson?.has_subscription,
      );

      const manualFullForHeader = Boolean(
        accessHasFullBookAccess(manualJson) ||
          accessHasFullCourseAccess(manualJson) ||
          accessHasFullBookAccess(booksJson) ||
          accessHasFullCourseAccess(coursesJson),
      );

      window.dispatchEvent(
        new CustomEvent("dear-language-profile-access-loaded", {
          detail: {
            hasWebsitePremium: revenueCatPremiumForHeader || manualFullForHeader,
          },
        }),
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      const message =
        error instanceof Error
          ? error.message
          : "Die Profildaten konnten nicht geladen werden.";

      setProfileError(message);
      setBookAccess(null);
      setCourseAccess(null);
      setManualAccess(null);
      setSubscriptionStatus(null);
    } finally {
      setLoading(false);
    }
  }, [cleanEmail, cleanUserId, isLoggedIn]);

  useEffect(() => {
    document.documentElement.classList.add("profile-screen-page");
    document.body.classList.add("profile-screen-page");

    return () => {
      document.documentElement.classList.remove("profile-screen-page");
      document.body.classList.remove("profile-screen-page");
    };
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const handlePortalOpened = (): void => {
    window.dispatchEvent(new CustomEvent("dear-language-profile-refresh"));
    void loadProfile();
  };

  return (
    <main className="profile-page">
      <UnSubscribe
        isOpen={showUnsubscribe}
        onClose={() => setShowUnsubscribe(false)}
        userEmail={cleanEmail}
        userId={cleanUserId}
        subscriptionIds={revenueCatSubscriptionIds}
        onPortalOpened={handlePortalOpened}
      />

      <section className="profile-container">
        <header className="profile-header-card">
          <div className="profile-header-content">
            <p className="profile-kicker">
              <FaShieldAlt />
              <span>Profil</span>
            </p>

            <h1 className="profile-page-title">Mein Profil</h1>

            <p className="profile-subtitle">
              Konto, Premium-Zugang, manueller Zugang, Bücherstatus und Kursstatus.
            </p>
          </div>

          <div className="profile-header-badge">
            <strong>{unlockedBookCount}/60</strong>
            <span>Bücher</span>
          </div>
        </header>

        {authLoading && (
          <div className="profile-notice profile-notice-info">
            <FaRedo className="profile-spin" />
            <span>Konto wird geprüft...</span>
          </div>
        )}

        {loading && (
          <div className="profile-notice profile-notice-info">
            <FaRedo className="profile-spin" />
            <span>Zugänge werden geprüft...</span>
          </div>
        )}

        {!authLoading && !isLoggedIn && (
          <div className="profile-notice profile-notice-error">
            <FaExclamationCircle />
            <span>Bitte loggen Sie sich ein, um Ihr Profil zu sehen.</span>
          </div>
        )}

        {profileError && (
          <div className="profile-notice profile-notice-error">
            <FaExclamationCircle />
            <span>{profileError}</span>
          </div>
        )}

        {!profileError && manualAccessError && (
          <div className="profile-notice profile-notice-warning">
            <FaExclamationCircle />
            <span>{manualAccessError}</span>
          </div>
        )}

        {!profileError && revenueCatError && (
          <div className="profile-notice profile-notice-warning">
            <FaExclamationCircle />
            <span>{revenueCatError}</span>
          </div>
        )}

        <div className="profile-dashboard-grid">
          <article className="profile-panel">
            <div className="profile-panel-head">
              <FaIdCard />
              <span>Konto</span>
            </div>

            <div className="profile-info-list">
              <div>
                <span>Name</span>
                <strong>{displayName}</strong>
              </div>

              <div>
                <span>E-Mail</span>
                <strong>{cleanEmail || "—"}</strong>
              </div>

              <div>
                <span>Benutzer-ID</span>
                <strong>{cleanUserId || "—"}</strong>
              </div>

              <div>
                <span>Erstellt</span>
                <strong>{createdAt}</strong>
              </div>

              <div>
                <span>Letzte Anmeldung</span>
                <strong>{lastSignInAt}</strong>
              </div>
            </div>
          </article>

          <article className="profile-panel">
            <div className="profile-panel-head">
              <FaCrown />
              <span>Premium-Zugang</span>
            </div>

            <div
              className={`profile-status-pill ${
                hasAnyAccess ? "profile-status-active" : "profile-status-inactive"
              }`}
            >
              {hasAnyAccess ? <FaCheckCircle /> : <FaExclamationCircle />}
              <span>
                {hasFullCourseAccess && hasFullBookAccess
                  ? "Vollzugang aktiv"
                  : hasRevenueCatWebsitePremium
                    ? isCancelledAutoRenewal(subscriptionStatus?.auto_renewal_status)
                      ? "Website Premium gekündigt"
                      : "Website Premium aktiv"
                    : hasManualAnyAccess
                      ? "Manueller Zugang aktiv"
                      : "Kein aktiver Zugang erkannt"}
              </span>
            </div>

            <div className="profile-chip-list">
              {activeSubscriptions.length > 0 ? (
                activeSubscriptions.map((item) => (
                  <span key={item} className="profile-access-chip active">
                    {item}
                  </span>
                ))
              ) : (
                <span className="profile-access-chip">Kein Abo</span>
              )}
            </div>

            {hasRevenueCatWebsitePremium && (
              <div className="profile-info-list profile-spaced-list">
                <div>
                  <span>Status</span>
                  <strong>
                    {formatSubscriptionStatus(subscriptionStatus?.status)}
                  </strong>
                </div>

                <div>
                  <span>Verlängerung</span>
                  <strong>
                    {formatAutoRenewal(subscriptionStatus?.auto_renewal_status)}
                  </strong>
                </div>

                <div>
                  <span>Zugang bis</span>
                  <strong>{subscriptionEndDate}</strong>
                </div>

                <div>
                  <span>Subscription ID</span>
                  <strong>{listOrEmpty(revenueCatSubscriptionIds)}</strong>
                </div>
              </div>
            )}

            {hasRevenueCatWebsitePremium && revenueCatSubscriptionIds.length > 0 && (
              <button
                type="button"
                className="profile-unsubscribe-button"
                onClick={() => setShowUnsubscribe(true)}
              >
                <FaBan />
                <span>Abo-Status ansehen</span>
              </button>
            )}
          </article>

          <article className="profile-panel">
            <div className="profile-panel-head">
              <FaBookOpen />
              <span>Bücherzugang</span>
            </div>

            <div className="profile-big-number">
              <strong>{unlockedBookCount}/60</strong>
              <span>freigeschaltete Bücher</span>
            </div>

            <div className="profile-chip-list">
              {accessibleBookLevels.length > 0 ? (
                accessibleBookLevels.map((level) => (
                  <span key={level} className="profile-access-chip active">
                    {level}
                  </span>
                ))
              ) : (
                <span className="profile-access-chip">Keine Bücher</span>
              )}
            </div>

            <div className="profile-info-list profile-spaced-list">
              <div>
                <span>Quelle</span>
                <strong>
                  {hasFullBookAccess
                    ? hasManualAnyAccess
                      ? "Manueller Vollzugang"
                      : "Website Premium"
                    : profileAccessSource}
                </strong>
              </div>
            </div>
          </article>

          <article className="profile-panel">
            <div className="profile-panel-head">
              <FaGraduationCap />
              <span>Kurse</span>
            </div>

            <div className="profile-big-number">
              <strong>
                {unlockedCourseCount}/{COURSE_LEVELS.length}
              </strong>
              <span>freigeschaltete Kurse</span>
            </div>

            <div className="profile-chip-list">
              {accessibleCourseLevels.length > 0 ? (
                accessibleCourseLevels.map((level) => (
                  <span key={level} className="profile-access-chip active">
                    {COURSE_NAMES[level] || level}
                  </span>
                ))
              ) : (
                <>
                  <span className="profile-access-chip">Kein Kursabo</span>
                  <span className="profile-access-chip">
                    Website Premium schaltet nur Bücher frei
                  </span>
                </>
              )}
            </div>
          </article>

          <article className="profile-panel profile-wide">
            <div className="profile-panel-head">
              <FaServer />
              <span>Manueller Zugang</span>
            </div>

            <div className="profile-info-list">
              <div>
                <span>Geprüft über</span>
                <strong>/api/accesses</strong>
              </div>

              <div>
                <span>Status</span>
                <strong>
                  {manualAccess
                    ? hasFullCourseAccess && hasFullBookAccess
                      ? "Aktiver manueller Vollzugang gefunden"
                      : "Aktiver manueller Zugang gefunden"
                    : "Kein manueller Zugang gefunden"}
                </strong>
              </div>

              <div>
                <span>Kurse</span>
                <strong>{listOrEmpty(accessibleCourseLevels)}</strong>
              </div>

              <div>
                <span>Bücher</span>
                <strong>{listOrEmpty(accessibleBookLevels)}</strong>
              </div>

              <div>
                <span>Notiz</span>
                <strong>{manualAccess?.note || "—"}</strong>
              </div>
            </div>
          </article>

          <article className="profile-panel profile-wide">
            <div className="profile-panel-head">
              <FaServer />
              <span>RevenueCat-Daten</span>
            </div>

            <div className="profile-info-list">
              <div>
                <span>Quelle</span>
                <strong>{profileAccessSource}</strong>
              </div>

              <div>
                <span>RevenueCat geprüft</span>
                <strong>
                  {bookAccess?.revenuecat_checked ||
                  courseAccess?.revenuecat_checked ||
                  subscriptionStatus?.success
                    ? "Ja"
                    : "Nein"}
                </strong>
              </div>

              <div>
                <span>Customer ID</span>
                <strong>{listOrEmpty(revenueCatCustomerIds)}</strong>
              </div>

              <div>
                <span>Gefundene E-Mail</span>
                <strong>{listOrEmpty(revenueCatMatchedEmails)}</strong>
              </div>

              <div>
                <span>Aktive Entitlements</span>
                <strong>{listOrEmpty(revenueCatActiveEntitlements)}</strong>
              </div>

              <div>
                <span>Aktive Produkte</span>
                <strong>{listOrEmpty(revenueCatActiveProducts)}</strong>
              </div>

              <div>
                <span>Subscription IDs</span>
                <strong>{listOrEmpty(revenueCatSubscriptionIds)}</strong>
              </div>
            </div>
          </article>

          {hasRevenueCatWebsitePremium &&
            isCancelledAutoRenewal(subscriptionStatus?.auto_renewal_status) && (
              <article className="profile-panel profile-wide">
                <div className="profile-panel-head">
                  <FaCheckCircle />
                  <span>Kündigung bestätigt</span>
                </div>

                <div className="profile-notice profile-notice-info">
                  <FaBan />
                  <span>
                    Ihr Website-Premium-Abo wurde gekündigt. Der Zugang bleibt
                    bis {subscriptionEndDate} aktiv. Danach entfernt RevenueCat
                    den Zugang automatisch.
                  </span>
                </div>
              </article>
            )}

          {!loading && !hasAnyAccess && (
            <article className="profile-panel profile-wide">
              <div className="profile-panel-head">
                <FaExclamationCircle />
                <span>Kein Zugang</span>
              </div>

              <div className="profile-notice profile-notice-warning">
                <FaExclamationCircle />
                <span>
                  Für diese E-Mail wurde kein Premium-Abo und kein manueller
                  Zugang gefunden.
                </span>
              </div>
            </article>
          )}
        </div>

        <div className="profile-actions">
          <button
            type="button"
            className="profile-secondary-button"
            onClick={() => void loadProfile()}
            disabled={loading}
          >
            <FaRedo className={loading ? "profile-spin" : ""} />
            <span>Daten aktualisieren</span>
          </button>
        </div>
      </section>
    </main>
  );
};

export default Profile;